import React, { useState, useEffect } from 'react';
import { 
  Share2, Facebook, Instagram, Linkedin, Plus, Trash2, Clock, 
  Send, Loader2, ExternalLink, AlertCircle, CheckCircle, Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { socialApi, adminApi } from '../../lib/api';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
];

const SocialMediaManager = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('accounts'); // accounts, posts, create
  const [newPost, setNewPost] = useState({
    platforms: [],
    content: '',
    image_url: '',
    link_url: '',
    scheduled_time: '',
    blog_post_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accountsRes, postsRes, blogsRes] = await Promise.all([
        socialApi.getAccounts(),
        socialApi.getScheduledPosts(),
        adminApi.getAllPosts()
      ]);
      setAccounts(accountsRes.data);
      setScheduledPosts(postsRes.data);
      setBlogPosts(blogsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPlatform = async (platform) => {
    try {
      let authUrl;
      if (platform === 'facebook' || platform === 'instagram') {
        const res = await socialApi.getFacebookAuthUrl();
        authUrl = res.data.auth_url;
      } else if (platform === 'linkedin') {
        const res = await socialApi.getLinkedInAuthUrl();
        authUrl = res.data.auth_url;
      }
      
      if (authUrl) {
        window.open(authUrl, '_blank', 'width=600,height=700');
        toast.info(`Complete the authorization in the popup window. Then refresh this page.`);
      }
    } catch (err) {
      if (err.response?.data?.detail?.includes('not configured')) {
        toast.error(`${platform} API credentials not configured. Please add them to your environment variables.`);
      } else {
        toast.error(`Failed to connect ${platform}`);
      }
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm('Disconnect this account?')) return;
    try {
      await socialApi.disconnectAccount(accountId);
      toast.success('Account disconnected');
      loadData();
    } catch (err) {
      toast.error('Failed to disconnect account');
    }
  };

  const handleSelectBlogPost = (blogPost) => {
    const siteUrl = window.location.origin.replace('/admin', '');
    setNewPost(prev => ({
      ...prev,
      blog_post_id: blogPost.id,
      content: `📰 New Blog Post: ${blogPost.title}\n\n${blogPost.excerpt || blogPost.content?.substring(0, 150) + '...'}\n\nRead more: ${siteUrl}/resources/${blogPost.slug}`,
      link_url: `${siteUrl}/resources/${blogPost.slug}`,
      image_url: blogPost.featured_image || ''
    }));
    setActiveTab('create');
  };

  const handleCreatePost = async () => {
    if (newPost.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    if (!newPost.content.trim()) {
      toast.error('Please enter post content');
      return;
    }

    try {
      const postData = {
        ...newPost,
        scheduled_time: newPost.scheduled_time || null
      };
      await socialApi.createPost(postData);
      toast.success(newPost.scheduled_time ? 'Post scheduled' : 'Post created');
      setNewPost({ platforms: [], content: '', image_url: '', link_url: '', scheduled_time: '', blog_post_id: '' });
      setActiveTab('posts');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleCancelPost = async (postId) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    try {
      await socialApi.cancelPost(postId);
      toast.success('Post cancelled');
      loadData();
    } catch (err) {
      toast.error('Failed to cancel post');
    }
  };

  const togglePlatform = (platformId) => {
    setNewPost(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Immediately';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      posted: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Social Media</h2>
          <p className="text-sm text-[#555555]">Connect accounts and schedule posts</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'accounts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('accounts')}
            className={activeTab === 'accounts' ? 'bg-[#1F2A44]' : ''}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Accounts
          </Button>
          <Button
            variant={activeTab === 'posts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('posts')}
            className={activeTab === 'posts' ? 'bg-[#1F2A44]' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled ({scheduledPosts.filter(p => p.status === 'pending').length})
          </Button>
          <Button
            variant={activeTab === 'create' ? 'default' : 'outline'}
            onClick={() => setActiveTab('create')}
            className={activeTab === 'create' ? 'bg-[#1F2A44]' : ''}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Accounts Tab */}
      {activeTab === 'accounts' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Setup Required</p>
              <p className="text-sm text-amber-700">
                To connect social media accounts, you need to configure API credentials in your environment:
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside">
                <li>Facebook/Instagram: FACEBOOK_APP_ID, FACEBOOK_APP_SECRET</li>
                <li>LinkedIn: LINKEDIN_CLIENT_ID, LINKEDIN_CLIENT_SECRET</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLATFORMS.map((platform) => {
              const connected = accounts.find(a => a.platform === platform.id);
              const Icon = platform.icon;
              
              return (
                <div key={platform.id} className="bg-white border border-[#E8E4DC] rounded p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${platform.color}20` }}>
                      <Icon className="w-6 h-6" style={{ color: platform.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#222222]">{platform.name}</h3>
                      {connected ? (
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Connected
                        </p>
                      ) : (
                        <p className="text-sm text-[#555555]">Not connected</p>
                      )}
                    </div>
                  </div>
                  
                  {connected ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-[#F5F3EF] rounded">
                        <p className="text-sm font-medium">{connected.account_name || connected.page_name}</p>
                        {connected.page_name && <p className="text-xs text-[#555555]">Page: {connected.page_name}</p>}
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full text-red-600 hover:bg-red-50"
                        onClick={() => handleDisconnect(connected.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      className="w-full"
                      style={{ backgroundColor: platform.color }}
                      onClick={() => handleConnectPlatform(platform.id)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Connect {platform.name}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scheduled Posts Tab */}
      {activeTab === 'posts' && (
        <div className="space-y-4">
          {scheduledPosts.length === 0 ? (
            <div className="bg-white border border-[#E8E4DC] rounded p-12 text-center">
              <Calendar className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Scheduled Posts</h3>
              <p className="text-[#555555] mb-4">Create a post to share on your social media profiles.</p>
              <Button onClick={() => setActiveTab('create')} className="bg-[#1F2A44]">
                <Plus className="w-4 h-4 mr-2" />
                Create Post
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledPosts.map((post) => (
                <div key={post.id} className="bg-white border border-[#E8E4DC] rounded p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {post.platforms.map(p => {
                          const platform = PLATFORMS.find(pl => pl.id === p);
                          const Icon = platform?.icon || Share2;
                          return <Icon key={p} className="w-4 h-4" style={{ color: platform?.color }} />;
                        })}
                        {getStatusBadge(post.status)}
                      </div>
                      <p className="text-[#222222] whitespace-pre-wrap">{post.content.substring(0, 200)}{post.content.length > 200 ? '...' : ''}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#555555]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.scheduled_time ? formatDateTime(post.scheduled_time) : 'Post immediately'}
                        </span>
                        <span>Created: {formatDateTime(post.created_at)}</span>
                      </div>
                    </div>
                    {post.status === 'pending' && (
                      <Button variant="outline" size="sm" onClick={() => handleCancelPost(post.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Post Tab */}
      {activeTab === 'create' && (
        <div className="space-y-4">
          {/* Quick Post from Blog */}
          {blogPosts.length > 0 && (
            <div className="bg-white border border-[#E8E4DC] rounded p-4">
              <h3 className="font-semibold text-[#222222] mb-3">Quick Post from Blog</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {blogPosts.slice(0, 6).map((blog) => (
                  <button
                    key={blog.id}
                    onClick={() => handleSelectBlogPost(blog)}
                    className="p-3 text-left border border-[#E8E4DC] rounded hover:border-[#1F2A44] hover:bg-[#FDFCFA] transition-colors"
                  >
                    <p className="font-medium text-sm text-[#222222] line-clamp-2">{blog.title}</p>
                    <p className="text-xs text-[#555555] mt-1">{blog.category}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Create Post Form */}
          <div className="bg-white border border-[#E8E4DC] rounded p-6 space-y-4">
            <h3 className="font-semibold text-[#222222]">Create New Post</h3>
            
            {/* Platform Selection */}
            <div>
              <Label className="mb-2 block">Select Platforms</Label>
              <div className="flex gap-4">
                {PLATFORMS.map((platform) => {
                  const connected = accounts.find(a => a.platform === platform.id);
                  const Icon = platform.icon;
                  const isSelected = newPost.platforms.includes(platform.id);
                  
                  return (
                    <button
                      key={platform.id}
                      onClick={() => connected && togglePlatform(platform.id)}
                      disabled={!connected}
                      className={`flex items-center gap-2 px-4 py-2 rounded border transition-all ${
                        isSelected 
                          ? 'border-[#1F2A44] bg-[#1F2A44] text-white' 
                          : connected 
                            ? 'border-[#E8E4DC] hover:border-[#1F2A44]' 
                            : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {platform.name}
                      {!connected && <span className="text-xs">(Not connected)</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Post Content */}
            <div>
              <Label>Post Content</Label>
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                className="mt-1"
                rows={5}
                placeholder="What do you want to share?"
              />
              <p className="text-xs text-[#555555] mt-1">{newPost.content.length} characters</p>
            </div>

            {/* Image & Link */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Image URL (Optional)</Label>
                <Input
                  value={newPost.image_url}
                  onChange={(e) => setNewPost(prev => ({ ...prev, image_url: e.target.value }))}
                  className="mt-1"
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Link URL (Optional)</Label>
                <Input
                  value={newPost.link_url}
                  onChange={(e) => setNewPost(prev => ({ ...prev, link_url: e.target.value }))}
                  className="mt-1"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <Label>Schedule (leave empty to post immediately)</Label>
              <Input
                type="datetime-local"
                value={newPost.scheduled_time}
                onChange={(e) => setNewPost(prev => ({ ...prev, scheduled_time: e.target.value }))}
                className="mt-1 w-64"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setNewPost({ platforms: [], content: '', image_url: '', link_url: '', scheduled_time: '', blog_post_id: '' })}>
                Clear
              </Button>
              <Button onClick={handleCreatePost} className="bg-[#1F2A44]">
                {newPost.scheduled_time ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Schedule Post
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Post Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaManager;
